import { useSyncExternalStore } from 'react';

import { envVariables } from '@/utils/env';

import { ALL_PERMISSIONS, Permission } from '../permissions';

const STORAGE_KEY = 'bam-permission-override';

type OverrideState = {
  isActive: boolean;
  permissions: Set<Permission>;
};

type StoredOverride = {
  isActive: boolean;
  permissions: Permission[];
};

let listeners: Array<() => void> = [];

function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getStoredOverride(): StoredOverride | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredOverride;
  } catch {
    return null;
  }
}

function getSnapshot(): OverrideState {
  if (!envVariables().featurePermissionSwitcher) {
    return { isActive: false, permissions: new Set() };
  }

  const stored = getStoredOverride();
  if (!stored?.isActive) {
    return { isActive: false, permissions: new Set() };
  }

  return {
    isActive: true,
    permissions: new Set(stored.permissions),
  };
}

// Cache the snapshot to maintain referential stability
let cachedSnapshot: OverrideState = getSnapshot();

function getCachedSnapshot(): OverrideState {
  return cachedSnapshot;
}

function refreshCache() {
  cachedSnapshot = getSnapshot();
  notifyListeners();
}

// Listen to storage events from other tabs
if (globalThis.window !== undefined) {
  globalThis.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      refreshCache();
    }
  });
}

/**
 * Reads the current permission override state from localStorage.
 * Only active when devToolsEnabled is true.
 */
export function usePermissionOverride(): OverrideState {
  return useSyncExternalStore(subscribe, getCachedSnapshot, () => ({
    isActive: false,
    permissions: new Set<Permission>(),
  }));
}

// --- Imperative API for the Permission Switcher UI ---

export function setPermissionOverride(permissions: Permission[]): void {
  const data: StoredOverride = { isActive: true, permissions };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  refreshCache();
}

export function clearPermissionOverride(): void {
  localStorage.removeItem(STORAGE_KEY);
  refreshCache();
}

export function togglePermission(permission: Permission): void {
  const stored = getStoredOverride();
  const current = new Set(stored?.permissions ?? ALL_PERMISSIONS);

  if (current.has(permission)) {
    current.delete(permission);
  } else {
    current.add(permission);
  }

  setPermissionOverride([...current]);
}

export function isOverrideActive(): boolean {
  const stored = getStoredOverride();
  return !!stored?.isActive && !!envVariables().featurePermissionSwitcher;
}
